import { useEffect } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

export default function LiquidBackground() {
  const { theme } = useTheme();
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smoother spring animation for more fluid, less jarring feel
  const springConfig = { damping: 35, stiffness: 100, mass: 0.8 };

  const x1 = useSpring(mouseX, springConfig);
  const y1 = useSpring(mouseY, springConfig);

  const x2 = useSpring(mouseX, { ...springConfig, damping: 40, mass: 1.2 });
  const y2 = useSpring(mouseY, { ...springConfig, damping: 40, mass: 1.2 });

  // Reduced movement range for subtler effect
  const x1Trans = useTransform(x1, (val) => val * -30);
  const y1Trans = useTransform(y1, (val) => val * -30);

  const x2Trans = useTransform(x2, (val) => val * 50);
  const y2Trans = useTransform(y2, (val) => val * 50);

  const xSpot = useTransform(x1, (val) => val * 15);
  const ySpot = useTransform(y1, (val) => val * 15);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;

      // Normalize coordinates -1 to 1
      const x = (clientX / innerWidth) * 2 - 1;
      const y = (clientY / innerHeight) * 2 - 1;

      mouseX.set(x);
      mouseY.set(y);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  const isDark = theme === 'dark';

  return (
    <div
      className={`fixed inset-0 -z-10 overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#030305]' : 'bg-[#E7E0D3]'
        }`}
    >
      {/* Deep Ambient Glow (Static Base) */}
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${isDark
            ? 'bg-[radial-gradient(circle_at_center,_rgba(25,25,30,0.7)_0%,_rgba(0,0,0,1)_100%)]'
            : 'bg-[radial-gradient(circle_at_center,_rgba(231,224,211,0.3)_0%,_rgba(231,224,211,0)_100%)]'
          }`}
      />

      {/* Fluid Layer 1: UF Orange (Moves opposite to mouse) - Subtler */}
      <motion.div
        className={`absolute top-1/4 left-1/4 w-[55vw] h-[55vw] rounded-full filter blur-[140px] transition-opacity duration-500 ${isDark ? 'mix-blend-screen opacity-[0.12]' : 'mix-blend-multiply opacity-[0.08]'
          }`}
        style={{
          background: 'radial-gradient(circle, var(--color-uf-orange) 0%, transparent 70%)',
          x: x1Trans,
          y: y1Trans,
        }}
      />

      {/* Fluid Layer 2: UF Blue (Moves with mouse, slightly lagged) - Subtler */}
      <motion.div
        className={`absolute bottom-1/4 right-1/4 w-[60vw] h-[60vw] rounded-full filter blur-[160px] transition-opacity duration-500 ${isDark ? 'mix-blend-screen opacity-[0.15]' : 'mix-blend-multiply opacity-[0.10]'
          }`}
        style={{
          background: 'radial-gradient(circle, var(--color-uf-blue) 0%, transparent 70%)',
          x: x2Trans,
          y: y2Trans,
        }}
      />

      {/* Interactive Spotlight (Follows mouse closely) - More subtle */}
      <motion.div
        className={`absolute inset-0 mix-blend-overlay transition-opacity duration-500 ${isDark ? 'opacity-20' : 'opacity-10'
          }`}
        style={{
          background: isDark
            ? `radial-gradient(500px circle at 50% 50%, rgba(255,255,255,0.08), transparent 40%)`
            : `radial-gradient(500px circle at 50% 50%, rgba(0,0,0,0.03), transparent 40%)`,
          x: xSpot,
          y: ySpot,
        }}
      />
    </div>
  );
}
